import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormGroup } from '@angular/forms';
import { Router, ActivatedRoute, IsActiveMatchOptions } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

// Local imports
import { CommonService } from '../../services/common.service';
import { AuthService } from '../../modules/authentication/services/auth.service';
import { Credential } from 'src/app/model/credential';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm: UntypedFormGroup;
  hide: boolean = true;
  loginInProgress: boolean = false;
  liveCountdownValue: number;
  companyRequired: boolean;
  issueLogin: boolean = false;
  isActiveMatchOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'exact',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  constructor(
    public cService: CommonService,
    public authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {
    this.cService.getLanguageList().subscribe();

    if (this.authService.doFullRefreshOnLogout) {
      this.cService.reloadAndTryToApplyUpdates(true);
    }
  }

  ngOnInit() {
    this.loginForm = new UntypedFormGroup({
      username: new UntypedFormControl(null, Validators.required),
      company: new UntypedFormControl(null),
      password: new UntypedFormControl(null, Validators.required),
    });

    if (location.href.toLowerCase().indexOf('login_with_company') > 0) {
      this.companyRequired = true;
      this.loginForm = new UntypedFormGroup({
        username: new UntypedFormControl(null, Validators.required),
        company: new UntypedFormControl(null, Validators.required),
        password: new UntypedFormControl(null, Validators.required),
      });
    }

    this.cService.userProfile = JSON.parse(localStorage.getItem(this.cService.localStorageKeys.userProfile));
    this.cService.applyUserProfile();

    // Process namespace login
    this.route.params.subscribe({
      next: (response: { namespace: string }) => {
        if (response.namespace) {
          localStorage.setItem('passwordResetNameSpace', response.namespace);
          this.authService.loginNamespace = response.namespace;
          if (this.cService.userProfile) {
            this.cService.userProfile.loginNamespace = response.namespace;
          }

          this.authService.clearUserProfileButPreserveSettings();
        } else {
          this.clearNamespace();
          this.authService.clearUserProfileButPreserveSettings();
        }
      },
    });

    this.dialog.closeAll();

    console.log('Profile when logged out', this.cService.userProfile);

    if (this.loginForm) {
      this.loginForm.valueChanges.subscribe({
        next: () => {
          if (!this.authService.messageHasCountdown) {
            this.authService.loginErrorMessage = '';
            this.authService.loginSuccessMessage = '';
          }
        },
      });
    }

    setTimeout(() => {
      this.cService.hideIndeterminateProgress = true;
    });
  }

  private clearNamespace() {
    this.authService.loginNamespace = '';
    if (this.cService.userProfile) {
      this.cService.userProfile.loginNamespace = '';
    }
    localStorage.removeItem('passwordResetNameSpace');
  }

  public clearLoginForm(callback?: () => void): void {
    this.loginForm.reset();
    this.authService.loginErrorMessage = '';

    if (callback) {
      callback();
    }
  }

  public logUserIn(): void {
    if (this.cService.passwordResetInProgress) {
      this.requestPasswordResetLink();
    } else {
      this.loginInProgress = true;
      this.cService.passwordResetInProgress = false;

      const credentials: Credential = {
        username: this.loginForm.get('username').value,
        company: this.loginForm.get('company').value,
        password: this.loginForm.get('password').value,
      };

      this.authService.login(credentials).subscribe({
        next: (response) => {
          if (response && response['startPage']) {
            this.cService.appSettings.defaultPage = response['startPage'];
          }

          this.router.navigate([this.cService.appSettings.defaultPage]).then(() => {
            this.cService.identifySelectedNavAndParent();
            this.cService.tryApplyingUpdatesIfAvailable();
            this.reloadIfStartPageNotActivated(this.cService.appSettings.defaultPage);
          });
        },

        error: (er) => {
          console.error('ERROR::', er);

          this.loginInProgress = false;
          this.authService.loginErrorMessage = this.cService.readyTranslations.messagesAndWarnings.signInFailed;
          if (er && er.errorBody) {
            if (er.errorBody.error == 'PASSWORD_RESET_REQUIRED') {
              this.authService.loginErrorMessage =
                this.cService.readyTranslations.messagesAndWarnings.passwordResetRequired;
            }
            if (er.errorBody.error == 'USER_HAS_MULTIPLE_COMPANIES') {
              this.companyRequired = true;
              this.loginForm.controls['company'].setValidators(Validators.required);
              this.authService.loginErrorMessage =
                this.cService.readyTranslations.messagesAndWarnings.signInFailedMultipleComapny;
            }
          }

          this.cService.hideIndeterminateProgress = true;
          this.authService.logout(true, true);
        },
      });
    }
  }

  /**
   * Incase attempted navigation to start page
   * after login is blocked by NavigationGuard,
   * user lands into system with login url.
   *
   * In this case reload.
   */
  private reloadIfStartPageNotActivated(urlAttemptedNavigation: string): void {
    const navigationSuccess = this.router.isActive(urlAttemptedNavigation, this.isActiveMatchOptions);

    if (!navigationSuccess) {
      location.reload();
    }
  }

  requestPasswordResetLink() {
    console.log('Password reset data', this.loginForm.value);
    this.authService.loginErrorMessage = '';

    this.authService.requestPasswordRestLink(this.loginForm.value).subscribe({
      next: () => {
        this.clearLoginForm(() => {
          this.authService.loginSuccessMessage =
            this.cService.readyTranslations.messagesAndWarnings.checkEmailToComplete;
        });
      },
      error: (er) => {
        if (er && er.errorBody) {
          if (er.errorBody.error == 'USER_HAS_MULTIPLE_COMPANIES') {
            this.companyRequired = true;
            this.loginForm.controls['company'].setValidators(Validators.required);
            this.authService.loginErrorMessage =
              this.cService.readyTranslations.messagesAndWarnings.signInFailedMultipleComapny;
            return;
          }
        }

        this.clearLoginForm(() => {
          this.authService.loginSuccessMessage =
            this.cService.readyTranslations.messagesAndWarnings.checkEmailToComplete;
        });
      },
    });
  }

  getLiveCountdown(liveCount: number) {
    this.liveCountdownValue = liveCount;

    if (!this.liveCountdownValue || this.liveCountdownValue === 0) {
      this.authService.messageHasCountdown = false;
      this.authService.loginErrorMessage = '';
    }
  }

  showLoginForm() {
    const showLoginForm = !this.authService.isAuthenticated() && !this.authService.doFullRefreshOnLogout;
    return showLoginForm;
  }

  commencePasswordReset() {
    this.cService.passwordResetInProgress = !this.cService.passwordResetInProgress;
    this.loginInProgress = false;
    this.authService.loginErrorMessage = '';
  }
}
